import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../../../../../../app/hooks";
import { clearAsiaPayoutData, setStep } from "../../../../redux/formDataSlice";
import { merchantBusinessKeys } from "../../../../../Settings/settingsSlice";
import { payoutRequestToAsia, setLoading } from "../../../../redux/slice";
import { encryptPayloadWithMerchantKeyJIT } from "../../../../../../utils/encryptionAlgorithm";
import { getErrorMessage } from "../../../../utils";
import { NetworkErrorAlertContext } from "../../../../../../context/NetworkErrorAlert";
import { SuccessAlertContext } from "../../../../../../context/SuccessAlertContext";
import { requestPayoutV2Otp } from "../../../../api";
import { FormInputs } from "../../../../../../types/walletTypes";
import { formatCurrencyAmount } from "../../../../../../utils/currency";
import {
  ButtonsWrapper,
  ContentWrapper,
  DetailsBox,
  DetailsContent,
  DetailsRow,
  DetailsSectionHeader,
  DetailsValue,
  EditButton,
  SectionTitle,
} from "../../../shared/styles";
import Loader from "../../../../../../components/common/Loader";
import WalletOTP from "../../../../components/SendFunds/components/WalletOTP";
import { DetailsLabel } from "../../../../../Settlements/SettlementPayout/styles";
import { Button } from "../../../../../../components/common/Button";
import { CountryConfig } from "../../../shared/types";

interface IndiaReviewFormProps {
  config?: CountryConfig;
  handleNext?: () => void;
  handlePrevious?: () => void;
}

const IndiaReviewForm: React.FC<IndiaReviewFormProps> = ({ handlePrevious }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const getSavedData: FormInputs = useAppSelector(
    (state) => (state.formData as any)?.asiaPayoutData?.inr || {}
  );
  const { businessId } = useAppSelector((state) => state.users);
  const { keys } = useAppSelector((state) => state.settings);
  const { loading } = useAppSelector((state) => state.wallets || {});

  const [verifyOTP, setVerifyOTP] = useState(false);

  const { onShowAlert: onShowSuccessAlert } = useContext(SuccessAlertContext) || {};
  const { onShowAlert: onShowErrorAlert } = useContext(NetworkErrorAlertContext) || {};

  useEffect(() => {
    if (businessId) {
      dispatch(merchantBusinessKeys(businessId));
    }
  }, [businessId, dispatch]);

  const publicKey = keys?.data?.publicKey;
  const sourceCurrency = getSavedData?.sourceCurrency || "USD";
  const currentDate = dayjs().format("DD.MM.YYYY");

  const handleEditStep = (stepIndex: number) => {
    if (loading) return;
    dispatch(setStep(stepIndex));
  };

  const handlePayout = async (otp?: string) => {
    try {
      if (!businessId) {
        onShowErrorAlert?.("", "Business information is not available. Please try again.");
        return;
      }

      const rawPayload: any = {
        quotationId: getSavedData.quotationId,
        requestId: getSavedData.reference,
        otp,
        purpose: getSavedData.purposeCode,
        description: getSavedData.description,
        name: getSavedData.accountHolderName,
        ifscCode: getSavedData.ifscCode,
        accountNumber: getSavedData.accountNumber,
        accountType: "savings",
        bankName: getSavedData.bankName,
        branchName: getSavedData.branchName,
        country: "IN",
        currency: "INR",
        transferType: "local",
        type: "business",
        reference: getSavedData.reference,
      };

      const { encryptedMessage, publicKey: jitPublicKey } = await encryptPayloadWithMerchantKeyJIT(
        businessId,
        rawPayload
      );
      const finalPayload = { message: encryptedMessage };
      const authToken = jitPublicKey || publicKey;

      const action = await dispatch(
        payoutRequestToAsia({ data: finalPayload, token: authToken })
      );

      if (payoutRequestToAsia.fulfilled.match(action)) {
        onShowSuccessAlert?.("Payout successful");
        dispatch(clearAsiaPayoutData({ country: "inr" }));
        navigate("/wallets");
      } else if (payoutRequestToAsia.rejected.match(action)) {
        onShowErrorAlert?.("", (action.payload as string) || "Payout failed");
      }
    } catch (error) {
      onShowErrorAlert?.("", getErrorMessage(error, "Payout failed"));
    }
  };

  const handleSendOTP = async () => {
    try {
      dispatch(setLoading(true));
      await requestPayoutV2Otp(getSavedData?.reference, publicKey);
      setVerifyOTP(true);
    } catch (error) {
      onShowErrorAlert?.("", getErrorMessage(error, "Failed to send OTP"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <ContentWrapper>
      <Loader isLoading={loading} />

      {verifyOTP && (
        <WalletOTP
          setVerifyOTP={setVerifyOTP}
          makePayment={handlePayout}
          mode="transaction"
          onResend={handleSendOTP}
        />
      )}

      <DetailsBox>
        <DetailsSectionHeader>
          <SectionTitle>Transaction details</SectionTitle>
          <EditButton onClick={() => handleEditStep(0)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary receives</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount("INR", getSavedData?.amount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Exchange rate</DetailsLabel>
            <DetailsValue>{getSavedData?.rate || 0}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Processing fee</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, getSavedData?.fee)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Total amount charged</DetailsLabel>
            <DetailsValue>
              {formatCurrencyAmount(sourceCurrency, getSavedData?.totalAmount)}
            </DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Payment method</DetailsLabel>
            <DetailsValue>Bank transfer</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Date</DetailsLabel>
            <DetailsValue>{currentDate}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Estimated delivery</DetailsLabel>
            <DetailsValue>1-3 days</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Purpose</DetailsLabel>
            <DetailsValue>{getSavedData?.description || "-"}</DetailsValue>
          </DetailsRow>
        </DetailsContent>

        <DetailsSectionHeader>
          <SectionTitle>Beneficiary details</SectionTitle>
          <EditButton onClick={() => handleEditStep(1)}>
            <span>Edit</span>
          </EditButton>
        </DetailsSectionHeader>

        <DetailsContent>
          <DetailsRow>
            <DetailsLabel>Beneficiary name</DetailsLabel>
            <DetailsValue>{getSavedData?.accountHolderName || "-"}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Bank name</DetailsLabel>
            <DetailsValue>{getSavedData?.bankName || "-"}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>IFSC code</DetailsLabel>
            <DetailsValue>{getSavedData?.ifscCode || "-"}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Account number</DetailsLabel>
            <DetailsValue>{getSavedData?.accountNumber || "-"}</DetailsValue>
          </DetailsRow>

          <DetailsRow>
            <DetailsLabel>Branch name</DetailsLabel>
            <DetailsValue>{getSavedData?.branchName || "-"}</DetailsValue>
          </DetailsRow>
        </DetailsContent>
      </DetailsBox>

      <ButtonsWrapper>
        <Button
          onClick={handlePrevious}
          theme="alternate"
          height="48px"
          width="203px"
          disabled={loading}
        >
          Back
        </Button>
        <Button
          onClick={handleSendOTP}
          label="Confirm"
          height="48px"
          disabled={loading}
          loading={loading}
        />
      </ButtonsWrapper>
    </ContentWrapper>
  );
};

export default IndiaReviewForm;
